import { mainApi } from '../../../../api/mainApi';
import { middlewareApi } from '../../../middleware/middlewareApi';

import { setDepartmentList, setDepartmentListinLine } from './departmentsSlice';

import {
  Department,
  DepartmentPayload,
  DepartmentsResponse,
  DepartmentsInLine,
  Status,
} from '../interfaces/departments.interface';
import { AccessState } from '../../../../types';

export const departmentsTags = middlewareApi.enhanceEndpoints({
  addTagTypes: ['Departments', 'DepartmentsAll'],
});
// Define a service using a base URL and expected endpoints
export const departmentsApi = departmentsTags.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * @GET all departments
     */
    getDepartments: builder.query<
      DepartmentsResponse,
      { profileId: number; page: number; perPage: number; search: string }
    >({
      queryFn: async ({ profileId, page, search, perPage }, { dispatch }) => {
        try {
          const { data } = await mainApi.get<DepartmentsResponse>('/departments', {
            params: {
              user_profile_id: profileId,
              page,
              size: perPage,
              type_sort: 'desc',
              search,
            },
          });

          const { data: dataInLine } = await mainApi.get<DepartmentsInLine[]>(
            '/departments/organizational',
            {
              params: {
                user_profile_id: profileId,
              },
            }
          );

          dispatch(setDepartmentList(data.data));
          dispatch(setDepartmentListinLine(dataInLine));

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: 'Departments', id } as const)),
              { type: 'Departments', id: 'LIST' },
            ]
          : [{ type: 'Departments', id: 'LIST' }],
    }),

    /**
     * @GET departaments list to combo box
     */
    getAvailableDepartments: builder.query<Department[], { profileId: number }>({
      queryFn: async ({ profileId }) => {
        try {
          const { data } = await mainApi.get<Department[]>('/departments', {
            params: {
              user_profile_id: profileId,
              data: 'all',
            },
          });

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: [{ type: 'Departments', id: 'LIST' }],
    }),

    /**
     * @GET department by id
     */
    getDepartment: builder.query<Department, { profileId?: number; departmentId: number }>({
      queryFn: async ({ profileId, departmentId }, { dispatch, getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = profileId || currentProfile?.profileId;
          const { data } = await mainApi.get<Department>(`/departments/${departmentId}`, {
            params: {
              user_profile_id,
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result, error, { profileId }) => [{ type: 'Departments', profileId }],
    }),

    /**
     * @POST create department
     */
    addDepartment: builder.mutation<
      Department,
      { profileId: number; departmentPayload: Partial<DepartmentPayload> }
    >({
      queryFn: async ({ profileId, departmentPayload }) => {
        try {
          const { data } = await mainApi.post<Department>('/departments', {
            user_profile_id: profileId,
            ...departmentPayload,
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [
        { type: 'Departments', id: 'LIST' },
        { type: 'DepartmentsAll', id: 'COMBO_DEPARTMENTS' },
      ],
    }),

    /**
     * @PUT update department
     */
    updateDepartment: builder.mutation<
      DepartmentPayload,
      { profileId: number; departmentId: number; departmentPayload: Partial<DepartmentPayload> }
    >({
      queryFn: async ({ profileId, departmentId, departmentPayload }) => {
        try {
          const { data } = await mainApi.put<DepartmentPayload>(`/departments/${departmentId}`, {
            user_profile_id: profileId,
            ...departmentPayload,
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [
        { type: 'Departments', id: 'LIST' },
        { type: 'DepartmentsAll', id: 'COMBO_DEPARTMENTS' },
      ],
    }),

    /**
     * @DELETE remove department
     */
    deleteDepartment: builder.mutation<
      DepartmentPayload,
      { profileId: number; departmentId: number }
    >({
      queryFn: async ({ profileId, departmentId }) => {
        try {
          const { data: dataStatus } = await mainApi.get<Status[]>('/status', {
            params: {
              user_profile_id: profileId,
              page: 1,
              size: 5,
              type_sort: 'desc',
              search: 'Eliminado',
            },
          });
          const { data } = await mainApi.put<DepartmentPayload>(
            `/departments/${departmentId}/status`,
            {
              user_profile_id: profileId,
              status_id: dataStatus[0].id,
            }
          );
          // const { data } = await mainApi.delete(`department/${departmentId}`, {
          //   data: {
          //     user_profile_id: profileId,
          //   },
          // });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [{ type: 'Departments', id: 'LIST' }],
    }),

    /**
     * @GET department by id
     */
    getDepartmentsAll: builder.query<Department[], { profileId: number }>({
      queryFn: async ({ profileId }, { dispatch }) => {
        try {
          const { data } = await mainApi.get<Department[]>('/departments', {
            params: {
              user_profile_id: profileId,
              data: 'all',
            },
          });

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: [{ type: 'DepartmentsAll', id: 'COMBO_DEPARTMENTS' }],
    }),
  }),
});
// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetDepartmentsQuery,
  useAddDepartmentMutation,
  useDeleteDepartmentMutation,
  useGetDepartmentQuery,
  useUpdateDepartmentMutation,
  useGetDepartmentsAllQuery,
  useGetAvailableDepartmentsQuery,
  useLazyGetDepartmentQuery,
} = departmentsApi;
